import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  BoxProps,
  BackgroundProps,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuItem,
  MenuList,
  Text,
  useColorModeValue,
  ButtonProps,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

export const formatSelectOptions = <T extends string>(
  values: readonly T[] | Record<any, T>,
  labelFormatting?: 'sentence' | 'title' | 'upper',
): {
  label: string;
  value: T;
}[] => {
  return _.sortBy(
    _.map(values, (value) => {
      let label: string;
      switch (labelFormatting) {
        case 'sentence':
          label = commonUtils.toSentenceCase(value);
          break;
        case 'title':
          label = commonUtils.toTitleCase(value);
          break;
        case 'upper':
          label = _.toUpper(value);
          break;
        default:
          label = value;
      }
      return {
        value,
        label,
      };
    }),
    'label',
  );
};

export interface SelectProps<T> {
  value: T | undefined;
  onChange: (nextValue: T | undefined) => void;
  options: {
    label: React.ReactNode;
    value: T;
    icon?: React.ReactElement;
  }[];
  isDisabled?: boolean;
  width?: BoxProps['width'];
  height?: string | number;
  placeholderLabel?: string;
  buttonVariant?: string;
  inputFloatingLabel?: string;
  inputFloatingLabelBackgroundColor?: BackgroundProps['bgColor'];
  isInvalid?: boolean;
  buttonProps?: MenuButtonProps & ButtonProps;
}

export function Select<T = string>({
  value,
  onChange,
  options,
  isDisabled,
  isInvalid,
  width,
  height,
  placeholderLabel,
  buttonVariant = 'outline',
  inputFloatingLabel,
  inputFloatingLabelBackgroundColor,
  buttonProps,
}: SelectProps<T>) {
  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const selectBgColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const placeholderTextColor = useColorModeValue('gray.400', 'whiteAlpha.400');

  if (!options || options.length === 0) {
    return null;
  }

  // if we are not explicitly told about isInvalid, and the given value is
  // not among the valid options, set invalid true to show the red border
  if (isInvalid === undefined && value && !selectedOption) {
    // eslint-disable-next-line no-param-reassign
    isInvalid = true;
  }

  const isInvalidProps = { borderColor: 'red.500', borderWidth: '1px' };

  return (
    <Flex flexDirection="column" flexGrow={1} maxWidth="100%">
      {inputFloatingLabel && (
        <Box h="0px" alignSelf="flex-start" zIndex="docked">
          {/* This wrapper container (Box) is needed to remove the space left when transforming the text component */}
          <Text
            style={{ transform: 'translate(16px,-8px)' }}
            bgColor={inputFloatingLabelBackgroundColor}
            fontSize="xs"
            p="0 1px"
            color="gray"
            borderRadius="sm"
          >
            {inputFloatingLabel}
          </Text>
        </Box>
      )}
      <Menu
        matchWidth
        autoSelect={false}
        eventListeners={{ scroll: false, resize: true }}
      >
        <MenuButton
          h={height}
          maxH={height}
          as={Button}
          variant={buttonVariant}
          textAlign="left"
          fontWeight="normal"
          leftIcon={selectedOption?.icon}
          rightIcon={<ChevronDownIcon />}
          overflowX="hidden"
          isDisabled={isDisabled}
          {...(isInvalid ? isInvalidProps : {})}
          {...buttonProps}
        >
          {(() => {
            if (!selectedOption) {
              return (
                <Text noOfLines={1} wordBreak="break-all">
                  {placeholderLabel}
                </Text>
              );
            }
            if (React.isValidElement(selectedOption.label)) {
              return selectedOption.label;
            }
            return (
              <Text noOfLines={1} wordBreak="break-all">
                {selectedOption.label}
              </Text>
            );
          })()}
        </MenuButton>
        <MenuList maxH="250" overflowY="auto" zIndex="dropdown" w={width}>
          {placeholderLabel && (
            <MenuItem key="placeholder" onClick={() => onChange(undefined)}>
              <Text color={placeholderTextColor}>{placeholderLabel}</Text>
            </MenuItem>
          )}
          {options.map(({ label, value: currentValue, icon }) => {
            return (
              <MenuItem
                key={`${JSON.stringify(currentValue)}`}
                onClick={() => onChange(currentValue)}
                background={currentValue === value ? selectBgColor : undefined}
                icon={icon}
              >
                {React.isValidElement(label) ? label : <Text>{label}</Text>}
              </MenuItem>
            );
          })}
        </MenuList>
      </Menu>
    </Flex>
  );
}
